import { action, computed, makeObservable, observable } from 'mobx';

import { mergeFlowData, resolveTransition, toPlainFlowData, validateFlowConfig } from './flowUtils';

/**
 * @typedef {import('./flowTypes').FlowConfig} FlowConfig
 * @typedef {import('./flowTypes').FlowNodeConfig} FlowNodeConfig
 */

/**
 * MobX-driven runtime session for a flow defined by FlowConfig.
 *
 * Owns:
 *  - currentNodeId
 *  - currentData (accumulated draft data)
 *  - history (visited node ids, used for Back)
 *
 * Step components must not mutate currentData directly — they call
 * commitStep(values), which merges + resolves the next transition.
 */
export class FlowController {
	/**
	 * @param {FlowConfig} flowConfig
	 * @param {Object} [initialData]
	 */
	constructor(flowConfig, initialData) {
		validateFlowConfig(flowConfig);

		this.flowConfig = flowConfig;
		this.initialData = initialData;

		this.currentNodeId = flowConfig.startNodeId;
		this.currentData = { ...(initialData || {}) };
		this.history = [];
		this.pendingDrafts = {};

		makeObservable(this, {
			currentNodeId: observable,
			currentData: observable,
			history: observable,
			pendingDrafts: observable,
			currentNode: computed,
			canGoBack: computed,
			isSummary: computed,
			isCurrentStepValid: computed,
			commitStep: action,
			goBack: action,
			reset: action,
			updateDraft: action,
		});
	}

	/** @returns {FlowNodeConfig} */
	get currentNode() {
		return this.flowConfig.nodes[this.currentNodeId];
	}

	/** @returns {boolean} */
	get canGoBack() {
		return this.history.length > 0;
	}

	/** @returns {boolean} */
	get isSummary() {
		return this.currentNode?.isSummary === true;
	}

	/**
	 * True when the current node has no `validate` rule, or when its rule
	 * passes for the merge of currentData + the current node's pending draft.
	 * @returns {boolean}
	 */
	get isCurrentStepValid() {
		const node = this.currentNode;
		if (!node || typeof node.validate !== 'function') return true;
		const merged = mergeFlowData(this.currentData, this.pendingDrafts[this.currentNodeId] || {});
		return node.validate(merged) === true;
	}

	/**
	 * Returns the pending draft for a node id (empty object if none stored).
	 * @param {string} nodeId
	 * @returns {Object}
	 */
	getPendingDraft(nodeId) {
		return this.pendingDrafts[nodeId] ? { ...this.pendingDrafts[nodeId] } : {};
	}

	/**
	 * Replaces the pending draft for a node id. The step component calls this
	 * on every local form change; the shell reads validity + commits it later.
	 * @param {string} nodeId
	 * @param {Object} values
	 */
	updateDraft(nodeId, values) {
		this.pendingDrafts[nodeId] = { ...(values || {}) };
	}

	/**
	 * Merge step values, resolve the next node, push history, advance.
	 * When called with no argument, uses the pending draft for the current node.
	 * @param {Object} [values]
	 */
	commitStep(values) {
		const stepValues = values !== undefined ? values : this.pendingDrafts[this.currentNodeId] || {};

		this.currentData = mergeFlowData(this.currentData, stepValues);
		const next = resolveTransition(
			this.flowConfig.transitions,
			this.currentNodeId,
			this.currentData
		);

		if (process.env.NODE_ENV !== 'production') {
			// eslint-disable-next-line no-console
			console.debug(
				`[FlowController] ${this.currentNodeId} -> ${next.to}`,
				next.name ? `(${next.name})` : ''
			);
		}

		const previous = this.currentNodeId;
		this.history.push(previous);
		this.currentNodeId = next.to;

		// The just-committed node's draft is now folded into currentData.
		delete this.pendingDrafts[previous];

		// Evict drafts for nodes that are no longer reachable (not in history
		// and not the new current). Keeps drafts on the same path preserved.
		this._evictOffPathDrafts();
	}

	/**
	 * Pop history and restore that node id verbatim.
	 * Does NOT recalculate transitions.
	 */
	goBack() {
		if (this.history.length === 0) return;
		const prev = this.history[this.history.length - 1];
		this.history.pop();
		this.currentNodeId = prev;
	}

	/**
	 * Restore start node, initial data, empty history.
	 * @param {Object} [initialData] optional new initial data
	 */
	reset(initialData) {
		if (initialData !== undefined) {
			this.initialData = initialData;
		}
		this.currentNodeId = this.flowConfig.startNodeId;
		this.currentData = { ...(this.initialData || {}) };
		this.history = [];
		this.pendingDrafts = {};
	}

	/**
	 * Remove pending drafts for nodes that are neither the current node nor in
	 * history. Drafts on nodes still on the visited path are preserved so that
	 * Back-then-Forward returns to a prefilled step.
	 * @private
	 */
	_evictOffPathDrafts() {
		const keep = new Set([this.currentNodeId, ...this.history]);
		for (const nodeId of Object.keys(this.pendingDrafts)) {
			if (!keep.has(nodeId)) delete this.pendingDrafts[nodeId];
		}
	}

	/**
	 * Returns a plain JS snapshot of the current draft data.
	 * @returns {Object}
	 */
	getData() {
		return toPlainFlowData(this.currentData);
	}

	/**
	 * Resolves the next node id for the current state without mutating anything.
	 * @returns {string}
	 */
	resolveNextNodeId() {
		const t = resolveTransition(this.flowConfig.transitions, this.currentNodeId, this.currentData);
		return t.to;
	}
}
